/* Rosabella sales page behaviours. Bands opt in with data attributes; no band ships its own JS. */

const SWIPER_URL = 'https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.mjs';
const teardowns = new WeakMap();

const $ = (root, sel) => root.querySelector(sel);
const $$ = (root, sel) => Array.from(root.querySelectorAll(sel));
const pad = (n) => String(n).padStart(2, '0');
const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function readJSON(root, sel) {
  const node = $(root, sel);
  if (!node) return null;
  try {
    return JSON.parse(node.textContent);
  } catch (err) {
    console.warn('[rosabella] bad JSON in', sel, err);
    return null;
  }
}

/* ---------------------------------------------------------------- Countdown + arrives-by */

function initClocks(root, add) {
  const boxes = $$(root, '[data-rsb-countdown]');
  const arrivals = $$(root, '[data-rsb-arrives]');
  if (!boxes.length && !arrivals.length) return;

  const ordinal = (d) => {
    if (d > 3 && d < 21) return 'th';
    return { 1: 'st', 2: 'nd', 3: 'rd' }[d % 10] || 'th';
  };

  // Calendar matches the rest of the store: 15 PageFly sections all do date + 3 plain days.
  // Business skips weekends, which is kinder but puts this page out of step with the PDPs.
  const addDays = (from, days, mode) => {
    const out = new Date(from);
    if (mode !== 'business') {
      out.setDate(out.getDate() + Math.max(0, days));
      return out;
    }
    let left = Math.max(0, days);
    while (left > 0) {
      out.setDate(out.getDate() + 1);
      const day = out.getDay();
      if (day !== 0 && day !== 6) left -= 1;
    }
    return out;
  };

  const tick = () => {
    const now = new Date();
    const end = new Date(now);
    end.setHours(24, 0, 0, 0);
    const rem = Math.max(0, Math.floor((end - now) / 1000));

    boxes.forEach((box) => {
      const h = $(box, '[data-rsb-cd="h"]');
      const m = $(box, '[data-rsb-cd="m"]');
      const s = $(box, '[data-rsb-cd="s"]');
      if (h) h.textContent = pad(Math.floor(rem / 3600));
      if (m) m.textContent = pad(Math.floor((rem % 3600) / 60));
      if (s) s.textContent = pad(rem % 60);
    });

    arrivals.forEach((el) => {
      const days = Number(el.getAttribute('data-rsb-arrives')) || 3;
      const date = addDays(now, days, el.getAttribute('data-rsb-arrives-mode'));
      const d = date.getDate();
      el.textContent = `${date.toLocaleDateString('en-US', { month: 'long' })} ${d}${ordinal(d)}`;
    });
  };

  tick();
  const id = window.setInterval(tick, 1000);
  add(() => window.clearInterval(id));
}

/* ---------------------------------------------------------------- UGC videos */

function initVideos(root, add) {
  const band = $(root, '[data-rsb-videos]');
  if (!band) return;

  const cards = $$(band, '[data-rsb-video-card]');
  if (!cards.length) return;

  const playLabel = band.getAttribute('data-play-label') || 'Play video';
  const pauseLabel = band.getAttribute('data-pause-label') || 'Pause video';

  const setPlaying = (card, playing) => {
    const btn = $(card, '[data-rsb-video-play]');
    const video = $(card, 'video');
    card.classList.toggle('is-playing', playing);
    if (!btn) return;
    const name = video && video.title ? `: ${video.title}` : '';
    btn.setAttribute('aria-label', `${playing ? pauseLabel : playLabel}${name}`);
  };

  // Only one testimonial plays at a time.
  const pauseOthers = (except) => {
    cards.forEach((card) => {
      if (card === except) return;
      const video = $(card, 'video');
      if (video && !video.paused) video.pause();
    });
  };

  cards.forEach((card) => {
    const video = $(card, 'video');
    const btn = $(card, '[data-rsb-video-play]');
    if (!video || !btn) return;

    const onClick = () => {
      if (!video.paused) {
        video.pause();
        return;
      }
      pauseOthers(card);
      video.muted = false;
      setPlaying(card, true);
      const p = video.play();
      if (p && p.catch) p.catch(() => setPlaying(card, false));
    };
    const onPlaying = () => {
      card.classList.add('is-started');
      setPlaying(card, true);
    };
    const onPause = () => setPlaying(card, false);
    const onEnded = () => {
      card.classList.remove('is-started');
      video.currentTime = 0;
    };

    btn.addEventListener('click', onClick);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onEnded);
    add(() => {
      btn.removeEventListener('click', onClick);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onEnded);
    });
  });
}

/* ---------------------------------------------------------------- Buy box */

function initBuyBox(root, add) {
  const box = $(root, '[data-rsb-buybox]');
  if (!box) return;

  const data = readJSON(box, '[data-rsb-buybox-data]');
  if (!data || !data.sets) return;

  const state = {
    perDay: String(data.perDay || 2),
    tier: Number(data.tier || 1)
  };
  if (!data.sets[state.perDay]) state.perDay = Object.keys(data.sets)[0];

  const tierList = $(box, '[data-rsb-tiers]');
  const variantInput = $(box, '[data-rsb-variant]');
  const planInput = $(box, '[data-rsb-plan]');
  const atcPrice = $(box, '[data-rsb-atc-price]');
  const atcCompare = $(box, '[data-rsb-atc-compare]');
  const stickyPrice = document.querySelector('[data-rsb-sticky-price]');
  const stickyCompare = document.querySelector('[data-rsb-sticky-compare]');
  const stickyLabel = document.querySelector('[data-rsb-sticky-label]');

  const tiers = () => data.sets[state.perDay].tiers;

  const render = () => {
    const set = tiers();
    if (state.tier >= set.length) state.tier = set.length - 1;
    const active = set[state.tier];

    $$(box, '[data-rsb-perday]').forEach((btn) => {
      const on = btn.getAttribute('data-rsb-perday') === state.perDay;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    if (tierList) {
      $$(tierList, '[data-rsb-tier]').forEach((card) => {
        const perDay = card.getAttribute('data-rsb-perday-set');
        const index = Number(card.getAttribute('data-rsb-tier'));
        const visible = perDay === state.perDay;
        card.hidden = !visible;
        const on = visible && index === state.tier;
        card.classList.toggle('is-selected', on);
        const radio = $(card, 'input[type="radio"]');
        if (radio) radio.checked = on;
      });
    }

    if (variantInput) variantInput.value = active.variant;
    if (planInput) planInput.value = active.plan || '';
    if (atcPrice) atcPrice.textContent = active.priceText;
    if (atcCompare) {
      atcCompare.textContent = active.compareText || '';
      atcCompare.hidden = !active.compareText;
    }
    if (stickyPrice) stickyPrice.textContent = active.priceText;
    if (stickyCompare) stickyCompare.textContent = active.compareText || '';
    if (stickyLabel) stickyLabel.textContent = active.label;
  };

  const onClick = (event) => {
    const perDay = event.target.closest('[data-rsb-perday]');
    if (perDay && box.contains(perDay)) {
      state.perDay = perDay.getAttribute('data-rsb-perday');
      render();
      return;
    }
    const tier = event.target.closest('[data-rsb-tier]');
    if (tier && box.contains(tier)) {
      state.tier = Number(tier.getAttribute('data-rsb-tier'));
      render();
    }
  };

  box.addEventListener('click', onClick);
  add(() => box.removeEventListener('click', onClick));

  const form = $(box, '[data-rsb-atc-form]');
  if (form) {
    const onSubmit = async (event) => {
      const after = form.getAttribute('data-rsb-after') || 'checkout';
      if (after === 'none') return;
      event.preventDefault();
      const button = $(form, 'button[type="submit"]');
      if (button) button.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(window.Shopify?.routes?.root
          ? `${window.Shopify.routes.root}cart/add.js`
          : '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{
              id: Number(variantInput.value),
              quantity: 1,
              selling_plan: planInput && planInput.value ? Number(planInput.value) : undefined
            }]
          })
        });
        if (!res.ok) throw new Error(await res.text());
        window.location.href = form.getAttribute('data-rsb-destination') || '/checkout';
      } catch (err) {
        console.warn('[rosabella] add to cart failed, falling back to a form post', err);
        if (button) button.removeAttribute('aria-busy');
        form.submit();
      }
    };
    form.addEventListener('submit', onSubmit);
    add(() => form.removeEventListener('submit', onSubmit));
  }

  render();

  // The hero and final CTAs name a fixed tier in their copy ("Claim Buy 2 + Get 2 FREE"),
  // so their price is that tier's and must not follow the shopper's selection.
  const pinned = data.sets[String(data.perDay)];
  const named = pinned && pinned.tiers[Number(data.tier)];
  if (named) {
    document.querySelectorAll('[data-rsb-live-price]').forEach((el) => {
      el.textContent = named.priceText;
    });
  }
}

/* ---------------------------------------------------------------- Gallery */

async function initGallery(root, add) {
  const gallery = $(root, '[data-rsb-gallery]');
  if (!gallery) return;

  const mainEl = $(gallery, '[data-rsb-gallery-main]');
  const thumbsEl = $(gallery, '[data-rsb-gallery-thumbs]');
  if (!mainEl) return;

  const thumbs = thumbsEl ? $$(thumbsEl, '[data-rsb-thumb]') : [];
  const mark = (index) => {
    thumbs.forEach((btn, i) => {
      const on = i === index;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-current', on ? 'true' : 'false');
    });
  };

  let Swiper;
  try {
    ({ default: Swiper } = await import(SWIPER_URL));
  } catch (err) {
    // No Swiper: the slides stay a plain scroll-snap strip, which is still usable.
    console.warn('[rosabella] Swiper unavailable, gallery stays a scroll strip', err);
    gallery.classList.add('is-static');
    const slides = $$(mainEl, '.swiper-slide');
    const onThumb = (event) => {
      const btn = event.target.closest('[data-rsb-thumb]');
      if (!btn) return;
      const i = thumbs.indexOf(btn);
      if (slides[i]) slides[i].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      mark(i);
    };
    if (thumbsEl) {
      thumbsEl.addEventListener('click', onThumb);
      add(() => thumbsEl.removeEventListener('click', onThumb));
    }
    mark(0);
    return;
  }

  const main = new Swiper(mainEl, {
    loop: true,
    speed: 350,
    spaceBetween: 12,
    grabCursor: true,
    keyboard: { enabled: true },
    a11y: { enabled: true }
  });

  // Thumbs are a CSS grid in both Figma frames, not a slider, so they are wired by hand.
  main.on('slideChange', () => mark(main.realIndex));
  const onThumb = (event) => {
    const btn = event.target.closest('[data-rsb-thumb]');
    if (!btn) return;
    main.slideToLoop(thumbs.indexOf(btn));
  };
  if (thumbsEl) {
    thumbsEl.addEventListener('click', onThumb);
    add(() => thumbsEl.removeEventListener('click', onThumb));
  }

  mark(main.realIndex);
  gallery.classList.add('is-ready');
  add(() => main.destroy(true, true));
}

/* ---------------------------------------------------------------- Sticky CTA */

function initSticky(root, add) {
  const bar = document.querySelector('[data-rsb-sticky]');
  const hero = $(root, '[data-rsb-hero]');
  if (!bar || !hero || !('IntersectionObserver' in window)) return;

  // The bar only exists to repeat the buy box CTA, so any band that already carries one
  // stands it down: the buy box itself, and the footer.
  const blockers = $$(root, '[data-rsb-sticky-hide]');
  const covered = new Set();
  let heroInView = true;
  const sync = () => bar.classList.toggle('is-shown', !heroInView && covered.size === 0);

  const heroIO = new IntersectionObserver(
    ([entry]) => {
      heroInView = entry.isIntersecting || entry.boundingClientRect.top > 0;
      sync();
    },
    { threshold: 0 }
  );
  heroIO.observe(hero);
  add(() => heroIO.disconnect());

  if (blockers.length) {
    const blockIO = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) covered.add(entry.target);
          else covered.delete(entry.target);
        });
        sync();
      },
      { threshold: 0 }
    );
    blockers.forEach((el) => blockIO.observe(el));
    add(() => blockIO.disconnect());
  }
}

/* ---------------------------------------------------------------- Reveal on scroll */

function initReveal(root, add) {
  const items = $$(root, '.rsb-reveal');
  if (!items.length) return;
  if (reduced() || !('IntersectionObserver' in window)) {
    items.forEach((el) => el.classList.add('is-in'));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
  );
  items.forEach((el) => io.observe(el));
  add(() => io.disconnect());
}

/* ---------------------------------------------------------------- Boot */

function mount(root) {
  if (!root || teardowns.has(root)) return;
  const fns = [];
  const add = (fn) => fns.push(fn);
  teardowns.set(root, fns);

  initClocks(root, add);
  initVideos(root, add);
  initBuyBox(root, add);
  initSticky(root, add);
  initReveal(root, add);
  initGallery(root, add);
}

function unmount(root) {
  const fns = teardowns.get(root);
  if (!fns) return;
  fns.forEach((fn) => {
    try {
      fn();
    } catch (err) {
      console.warn('[rosabella] teardown failed', err);
    }
  });
  teardowns.delete(root);
}

const mountAll = () => document.querySelectorAll('[data-rsb-root]').forEach(mount);

document.documentElement.classList.add('rsb-js');

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountAll, { once: true });
} else {
  mountAll();
}

document.addEventListener('shopify:section:load', (event) => {
  const root = event.target.querySelector('[data-rsb-root]');
  if (root) mount(root);
});

document.addEventListener('shopify:section:unload', (event) => {
  const root = event.target.querySelector('[data-rsb-root]');
  if (root) unmount(root);
});
